/**
 * CodeRdBadge — the universal visual signature of a Shariti matricule.
 *
 * Renders wherever the code_rd of a candidate must be spotted at a
 * glance: candidate profile header, Kanban cards (every column, not
 * just Signé), embauche drawer under the name, notifications' details,
 * etc. One click copies the code to the clipboard with a "AT0304
 * copié" toast so the ops assistant can paste it directly into
 * Evergiving.
 *
 * Behaviour:
 *   - `code` present → monospace pill in shariti-orange, click-to-copy
 *     (falls back to `document.execCommand` if `navigator.clipboard`
 *     is unavailable, e.g. old Safari or non-secure contexts).
 *   - `code` absent AND `showMissing` → dashed "Sans code" pill (used
 *     only where the missing state is actionable — e.g. Kanban Signé).
 *   - `code` absent AND !showMissing → renders nothing.
 *
 * Size variants match the surrounding density:
 *   - "sm" (default) : Kanban card / drawer.
 *   - "lg"           : candidate profile header.
 *
 * Copying uses `event.stopPropagation()` + `preventDefault()` so a
 * badge sitting inside a `<Link>` (like on a card) doesn't navigate
 * away when the admin only wants the code in their clipboard.
 */
import { Fingerprint, Copy } from "lucide-react";
import { toast } from "sonner";


async function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (_e) { /* fall through */ }
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return true;
    } catch (_e) {
        return false;
    }
}


export default function CodeRdBadge({
    code,
    candidateId,
    size = "sm",
    showMissing = false,
    onCopy,
}) {
    const dims = size === "lg"
        ? "text-sm px-3 py-1"
        : "text-[10px] px-2 py-0.5";
    const iconSize = size === "lg" ? "h-3.5 w-3.5" : "h-2.5 w-2.5";

    if (!code) {
        if (!showMissing) return null;
        return (
            <span
                data-testid={`code-rd-badge-missing-${candidateId || "generic"}`}
                title="Matricule non attribué — action requise (Étape 4 : Rattrapage)"
                className={`inline-flex items-center gap-1 rounded-md font-mono font-black tracking-wider bg-slate-100 text-slate-400 border border-dashed border-slate-300 whitespace-nowrap ${dims}`}
            >
                <Fingerprint className={iconSize} />
                Sans code
            </span>
        );
    }

    async function handleClick(e) {
        e.preventDefault();
        e.stopPropagation();
        const ok = await copyToClipboard(code);
        if (ok) {
            toast.success(`${code} copié`, { duration: 1800 });
            onCopy?.(code);
        } else {
            toast.error("Copie impossible — copiez à la main");
        }
    }

    // Défensif contre la confusion typographique lettre O vs chiffre 0 :
    // seul code "ATO184" contient une LETTRE O (exception nominative
    // Eric Tutu Moparte). On la surligne visuellement pour lever toute
    // ambiguïté avec AT0184 (chiffre 0 = reserved à vie).
    const isLiteralO = code === "ATO184";
    const displayed = isLiteralO
        ? (
            <span className="inline-flex items-baseline">
                AT<span className="mx-[1px] px-1 py-[1px] rounded bg-emerald-500 text-white text-[0.85em] tracking-normal">O</span>184
            </span>
        )
        : code;

    return (
        <button
            type="button"
            onClick={handleClick}
            data-testid={`code-rd-badge-${candidateId || "generic"}`}
            title={isLiteralO
                ? "ATO184 — LETTRE O (exception nominative Eric Tutu Moparte). Ne pas confondre avec AT0184 (chiffre 0, reserved à vie)."
                : `Matricule Shariti ${code} — cliquer pour copier`}
            className={`group inline-flex items-center gap-1 rounded-md font-mono font-black tracking-wider bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/60 hover:bg-shariti-orange hover:text-white hover:border-shariti-orange transition whitespace-nowrap shrink-0 cursor-pointer ${dims}`}
        >
            <Fingerprint className={iconSize} />
            {displayed}
            <Copy className={`${iconSize} opacity-60 group-hover:opacity-100 transition`} />
        </button>
    );
}
